import { computed, effect, inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { combineLatest, map } from 'rxjs';

import { ApplicationModulesFacade } from '@kros-sk/application-modules';
import { ApplicationPersonsStore } from '@kros-sk/application-persons';
import { CompanyModuleRouterFacade } from '@kros-sk/router-stores/module-router-store';
import { DeviceDetectorFacade } from '@kros-sk/device-detector';

import { ApplicationCurrentSubmoduleStore } from './current-submodule/application-current-submodule.store';
import { ApplicationSubmodulesStore } from './submodules/application-submodules.store';

@Injectable()
export class ApplicationSubmodulesFacade {
  readonly #currentSubmoduleStore = inject(ApplicationCurrentSubmoduleStore);
  readonly #deviceDetector = inject(DeviceDetectorFacade);
  readonly #personStore = inject(ApplicationPersonsStore);
  readonly #routerService = inject(CompanyModuleRouterFacade);
  readonly #submodulesStore = inject(ApplicationSubmodulesStore);
  readonly #modulesFacade = inject(ApplicationModulesFacade);

  /** List of available submodules */
  readonly submodules = this.#submodulesStore.entities;

  readonly defaultSubmodule = computed(() => this.submodules().find((s) => s.isDefault));

  /** Name of the currently active submodule, resolved from the router slug */
  readonly currentSubmoduleName = computed(() => {
    const slug = this.#routerService.submodule();
    return this.submodules().find((s) => s.slug === slug)?.name ?? slug;
  });

  /** Current submodule json schema */
  readonly currentSchema = this.#currentSubmoduleStore.schema;

  /** The schema load itself, for the shell that renders it through a root outlet. */
  readonly currentSchemaState = this.#currentSubmoduleStore.state;
  readonly currentSchema$ = toObservable(this.currentSchema);

  readonly loadedModule = this.#submodulesStore.loadedModule;

  readonly isLoadingSubmodules$ = this.#submodulesStore.isLoading$;

  readonly isLoadingCurrentSubmodule$ = this.#currentSubmoduleStore.isLoading$;

  readonly isLoading$ = combineLatest([this.isLoadingSubmodules$, this.isLoadingCurrentSubmodule$]).pipe(
    map(([submodules, current]) => submodules || current)
  );

  readonly #deviceType = computed(() => {
    if (this.#deviceDetector.isMobile()) {
      return 'mobile';
    }

    return null;
  });

  constructor() {
    this.#submodulesStore.load(
      computed(() => ({
        module: this.#modulesFacade.currentModule(),
        personId: this.#personStore.currentPersonId(),
        device: this.#deviceType(),
      }))
    );

    effect(() => {
      this.#currentSubmoduleStore.setCriteria({
        submodule: this.#routerService.submodule(),
        personId: this.#personStore.currentPersonId(),
        device: this.#deviceType(),
      });
    });
  }
}
